import _ from 'underscore';
import PropTypes from 'prop-types';
import React, { useContext, useMemo, useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { Link } from '@fluentui/react';

import { conversionTagProp, withConversionTagData, useInitConversionTagData, isDataReady } from '@bingads-webui-campaign-react/conversion-data';

import { TagSVG } from '../components/tag-SVG';
import { CopyTag } from '../components/copy-tag';
import { WordpressInstructions } from '../components/wordpress-instructions';
import { platforms, useHelpIds } from '../utils/platforms';
import { getThirdPartyTagViewStyle } from '../styles/third-party-tag-view-styles';
import { ShopifyInstructions } from '../components/shopify-instructions';
import { SquareSpaceInstructions } from '../components/squarespace-instructions';
import { GoogleTagManagerInstructions } from '../components/gtm-instruction';
import { WooCommerceInstructions } from '../components/woocommerce-instructions';
import { WixInstructions } from '../components/wix-instructions';

const ThirdPartyTagViewUnstyled = (props) => {
  const {
    tagContext,
    selectedPlatform,
    isSmartOrExpertMode,
    classes,
    serverContext,
    logTrace,
    onGoogleLoggedOut,
    onGoogleSelectAccount,
    updateGTMImportContext,
    deps,
    finishPageData,
    gtmUIContext,
    customLink,
  } = props;
  const {
    data,
  } = tagContext;
  const i18n = useContext(I18nContext);
  const helpIds = useHelpIds();
  const allPlatformsLearnMoreLink = helpIds.allPlatformsLearnMore;

  const content = useMemo(() => i18n.getString(_TL_('{0} instructions')), [i18n]);
  const texts = useMemo(() => content.split('{0}'), [content]);

  const [showHelp, setShowHelp] = useState(true);

  useInitConversionTagData(tagContext);

  useEffect(() => {
    if (isDataReady(data) && !_.isString(allPlatformsLearnMoreLink) && showHelp) {
      logTrace('[ThirdPartyTagView] Show apex help content');
      const apexHelpLink = document.querySelector('.third-party-tag-view span > a.HelpTopic');
      if (apexHelpLink) {
        apexHelpLink.click();
      }
    }
  }, [data, showHelp, logTrace, allPlatformsLearnMoreLink]);

  const onCopyCallback = useCallback(({ error, message, tagContent }) => {
    if (error) {
      logTrace(`[ThirdPartyTagStep] Copy error. Error: ${error} tagContent: ${JSON.stringify(tagContent)}`);
      notificationApi.showWarning(message);
    } else {
      logTrace('[ThirdPartyTagStep] Copy success.');
      notificationApi.showInfo(message);
    }
  }, [logTrace]);

  const specificInstructionsForPlatform = useMemo(() => {
    if (!isSmartOrExpertMode) {
      // platform-specific instructions only apply for Smart Campaigns and Expert
      return null;
    }

    switch (selectedPlatform) {
      case 'WordPress':
        setShowHelp(false);
        return (
          <WordpressInstructions
            tagData={{
              cid: serverContext && serverContext.CurrentAccount && serverContext.CurrentAccount.AdvertiserCustomerID,
              aid: serverContext && serverContext.CurrentAccount && serverContext.CurrentAccount.Id,
              tagId: isDataReady(data) && data.id,
            }}
            logTrace={logTrace}
            serverContext={serverContext}
          />
        );
      case 'Shopify':
        setShowHelp(false);
        return (
          <ShopifyInstructions
            tag={data}
            logTrace={logTrace}
          />
        );
      case 'SquareSpace':
        setShowHelp(false);
        return (
          <SquareSpaceInstructions
            tag={data}
            logTrace={logTrace}
          />
        );
      case 'GoogleTagManager':
        setShowHelp(false);
        return (
          <GoogleTagManagerInstructions
            tag={data}
            serverContext={serverContext}
            onGoogleLoggedOut={onGoogleLoggedOut}
            onGoogleSelectAccount={onGoogleSelectAccount}
            updateGTMImportContext={updateGTMImportContext}
            deps={deps}
            finishPageData={finishPageData}
            gtmUIContext={gtmUIContext}
            logTrace={logTrace}
          />
        );
      case 'WooCommerce':
        setShowHelp(false);
        return (
          <WooCommerceInstructions
            tagData={{
              cid: serverContext && serverContext.CurrentAccount && serverContext.CurrentAccount.AdvertiserCustomerID,
              aid: serverContext && serverContext.CurrentAccount && serverContext.CurrentAccount.Id,
              tagId: isDataReady(data) && data.id,
            }}
            logTrace={logTrace}
            serverContext={serverContext}
          />
        );
      case 'Wix':
        setShowHelp(false);
        return (
          <WixInstructions
            tag={data}
            logTrace={logTrace}
          />
        );
      default:
        return null;
    }
  }, [isSmartOrExpertMode, data, selectedPlatform, serverContext, logTrace, onGoogleLoggedOut, onGoogleSelectAccount, updateGTMImportContext, deps, finishPageData, gtmUIContext]);

  const genericInstructions = (
    <div>
      {isDataReady(data) && (
        <React.Fragment>
          <TagSVG tag={data} />
          <CopyTag tag={data} onCopyCallback={onCopyCallback} />

          {_.isString(allPlatformsLearnMoreLink) ? (
            <Link className="all-platforms-learn-more" href={allPlatformsLearnMoreLink} target="_blank">
              {i18n.getString(_TL_('Learn more'))}
            </Link>
          ) : (customLink || (
            <LearnMoreLink
              topic={`${_.get(helpIds, [data.type, selectedPlatform])}`}
              linkText={`${texts[0]}${platforms[selectedPlatform]}${texts[1]}`}
            />
          ))}
        </React.Fragment>
      )}
    </div>
  );

  return (
    <div className={classNames(classes.container, 'third-party-tag-view')}>
      {isDataReady(data) && (
        specificInstructionsForPlatform || genericInstructions
      )}
    </div>
  );
};

ThirdPartyTagViewUnstyled.propTypes = {
  tagContext: conversionTagProp.isRequired,
  selectedPlatform: PropTypes.string.isRequired,
  isSmartOrExpertMode: PropTypes.bool,
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.shape({
      Id: PropTypes.number,
      AdvertiserCustomerID: PropTypes.number,
    }),
  }).isRequired,
  logTrace: PropTypes.func.isRequired,
  onGoogleLoggedOut: PropTypes.func.isRequired,
  onGoogleSelectAccount: PropTypes.func.isRequired,
  deps: PropTypes.shape({}).isRequired,
  updateGTMImportContext: PropTypes.func.isRequired,
  finishPageData: PropTypes.shape({}),
  gtmUIContext: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({}),
  }),
  classes: PropTypes.shape(_.mapObject(
    getThirdPartyTagViewStyle(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  customLink: PropTypes.elementType,
};

ThirdPartyTagViewUnstyled.defaultProps = {
  finishPageData: null,
  isSmartOrExpertMode: false,
  customLink: null,
  permissions: {
    dynamic: {},
  },
};

export const ThirdPartyTagView = withDefaultStyles(
  withConversionTagData(ThirdPartyTagViewUnstyled),
  getThirdPartyTagViewStyle
);
